import { useState } from 'react';
import styles from './OptionGroup.module.css';

/**
 * One collapsible category of settings — "Scoring", "Appearance", "Answers",
 * "Deadline & locking" — inside a question's editor (PLAN.md Phase 10.12).
 *
 * The editor outgrew a flat column of every setting: a Baby Shower bolão
 * opens with 18 question blocks, and a simple two-answer question buried its
 * own answers under scoring modes, layout choices and deadline pickers, all
 * expanded whether or not anyone needed them. Grouping puts the decisions
 * that matter up front and folds the rest away, without removing anything.
 *
 * Its own component (not inlined per screen) because both the creation
 * editor (`QuestionTypeBlockEditor`) and the later rules editor
 * (`EditQuestionResultsScreen`) use the exact same categories for the exact
 * same fields — an organizer moving between "author a question" and "revisit
 * its rules" should find "Scoring" in the same place both times, and a
 * second hand-copied implementation is how that stops being true.
 *
 * Open/closed is local, deliberately: it is a reading preference for this one
 * editing session, not part of the question, so it is never submitted and
 * never persisted.
 */
export default function OptionGroup({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.optionGroup}>
      <button
        type="button"
        className={styles.optionGroupToggle}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{title}</span>
        <span className={styles.optionGroupChevron} aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>
      {open ? <div className={styles.optionGroupBody}>{children}</div> : null}
    </div>
  );
}
